import { Box, Flex, Paper, Skeleton, Stack, useMantineTheme, createStyles } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import { Button, ButtonLink } from '~/domains/design-system/Button';
import { SuccessCheckmark } from '~/domains/design-system/icons/SuccessCheckmark';
import { useWalletBalance } from '~/domains/payments/hooks/useWalletBalance';
import { formatDollars } from '~/domains/common/utils/formatters';
import type { IPostWalletsDepositsResponse } from '~/services/wallet/interfaces/wallets.interface';
import dt from '~/testing';

interface DepositCompletedProps {
  onClose?: React.MouseEventHandler;
  depositAmount: number;
  depositResponse?: IPostWalletsDepositsResponse;
  contestId?: string;
  redirectTo?: string;
}

// Design-specific container widths not available in theme spacing
const CONTENT_MAX_WIDTH = 480;
const BUTTON_MIN_WIDTH = 440;

const useStyles = createStyles((theme) => ({
  paper: {
    border: `1px solid ${theme.colors.gray2[0]}`,
  },
  balanceRow: {
    borderBottom: `1px solid ${theme.colors.gray2[0]}`,
  },
  buttonContainer: {
    marginTop: theme.other.spacing._24,
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: CONTENT_MAX_WIDTH,
    width: '100%',
    minWidth: BUTTON_MIN_WIDTH,
    // Remove minWidth on mobile to allow button to fit screen
    [theme.fn.smallerThan('sm')]: {
      minWidth: 0,
    },
  },
}));

/**
 * Extracts deposit response data from API response structure.
 *
 * All deposit methods return the same structure: { id: string, data: { userId, status, bonusCreditsAssigned?, addedToWalletAmount? } }
 *
 * @param depositResponse - The deposit response from API
 * @returns Object containing extracted bonusCreditsAssigned and addedToWalletAmount
 */
function extractDepositResponseData(depositResponse?: IPostWalletsDepositsResponse) {
  if (!depositResponse?.data) {
    return { bonusCreditsAssigned: undefined, addedToWalletAmount: undefined };
  }

  return {
    bonusCreditsAssigned: depositResponse.data.bonusCreditsAssigned,
    addedToWalletAmount: depositResponse.data.addedToWalletAmount,
  };
}

function DepositCompleted({
  onClose,
  depositAmount,
  depositResponse,
  contestId,
  redirectTo,
}: DepositCompletedProps) {
  const { data: balance, isFetching } = useWalletBalance();
  const { t } = useTranslation('payments');
  const theme = useMantineTheme();
  const { classes } = useStyles();

  const { bonusCreditsAssigned, addedToWalletAmount } = extractDepositResponseData(depositResponse);

  // Use addedToWalletAmount from response if available, otherwise fall back to depositAmount prop
  const displayAmount = addedToWalletAmount ?? depositAmount;

  // Calculate cash balance from availableBalance - credits
  const cashBalance =
    balance?.availableBalance != null && balance?.credits != null
      ? balance.availableBalance - balance.credits
      : null;

  // Determine button text: "Continue" if contestId or redirectTo exists, otherwise "Explore contests"
  const hasRedirectOrContest = !!(contestId || redirectTo);
  const buttonText = hasRedirectOrContest
    ? t('deposits.completed.continue')
    : t('deposits.completed.exploreContests');

  const continueButton = (
    <Box className={classes.buttonContainer}>
      {onClose ? (
        <Button
          onClick={onClose}
          data-test-id={dt.payments.deposits.depositCompletedModal.continueButton}
          variant="primary-fill"
          size="large"
          fullWidth
        >
          {buttonText}
        </Button>
      ) : (
        <ButtonLink
          href="/contest-lobby"
          data-test-id={dt.payments.deposits.depositCompletedModal.continueButton}
          variant="primary-fill"
          size="large"
          fullWidth
        >
          {buttonText}
        </ButtonLink>
      )}
    </Box>
  );

  return (
    <Stack align="center" justify="center" w="100%" h="100%">
      <SuccessCheckmark />

      <Stack spacing={theme.other.spacing._16} align="center" w="100%">
        <Title variant="headline-xxxl">{t('deposits.completed.title')}</Title>

        <Stack spacing={0} align="center" w="100%">
          {/* Deposit summary - always shown, 8px spacing from title */}
          <Text
            variant="title-medium"
            mt={theme.other.spacing._8}
            data-test-id={dt.payments.deposits.depositCompletedModal.successMessage}
          >
            {t('deposits.completed.fundsAdded', {
              amount: formatDollars(displayAmount),
            })}
          </Text>

          {/* Bonus credits - conditionally shown with 4px spacing from deposit message */}
          {bonusCreditsAssigned != null && bonusCreditsAssigned > 0 && (
            <Text variant="title-small" c="gray2.6" mt={theme.other.spacing._4}>
              {t('deposits.completed.bonusCreditsMessage', {
                bonusAmount: formatDollars(bonusCreditsAssigned),
              })}
            </Text>
          )}
        </Stack>
      </Stack>

      {/* Balance Breakdown */}
      <Box
        mt={theme.other.spacing._32}
        w="100%"
        maw={CONTENT_MAX_WIDTH}
        data-test-id={dt.payments.deposits.depositCompletedModal.newBalance}
      >
        <Skeleton visible={isFetching} /* eslint-disable-line local-rules/use-theme-tokens */ radius={12}>
          <Paper p={0} px={theme.other.spacing._16} /* eslint-disable-line local-rules/use-theme-tokens */ radius={12} className={classes.paper} w="100%">
            <Stack spacing={0} w="100%">
              {/* Total Balance */}
              <Flex
                justify="space-between"
                align="flex-start"
                py={theme.other.spacing._12}
                className={classes.balanceRow}
              >
                <Stack spacing={0}>
                  <Text variant="subhead-medium" c="gray2.6">
                    {t('deposits.completed.balanceBreakdown.total')}
                  </Text>
                  <Text variant="body-large" c="gray2.6">
                    {t('deposits.completed.balanceBreakdown.totalLabel')}
                  </Text>
                </Stack>
                {balance?.availableBalance != null ? (
                  <Text variant="subhead-x-large" c="blue.7">
                    {formatDollars(balance.availableBalance)}
                  </Text>
                ) : (
                  <Text variant="body-large" c="gray2.6">
                    {t('deposits.completed.balanceUnavailable')}
                  </Text>
                )}
              </Flex>

              {/* Cash Balance */}
              <Flex
                justify="space-between"
                align="flex-start"
                py={theme.other.spacing._12}
                className={classes.balanceRow}
              >
                <Stack spacing={0}>
                  <Text variant="subhead-medium" c="gray2.6">
                    {t('deposits.completed.balanceBreakdown.cash')}
                  </Text>
                  <Text variant="body-large" c="gray2.6">
                    {t('deposits.completed.balanceBreakdown.cashLabel')}
                  </Text>
                </Stack>
                {cashBalance != null ? (
                  <Text variant="subhead-large" c="gray2.6">
                    {formatDollars(cashBalance)}
                  </Text>
                ) : (
                  <Text variant="body-large" c="gray2.6">
                    {t('deposits.completed.balanceUnavailable')}
                  </Text>
                )}
              </Flex>

              {/* Credits Balance */}
              <Flex justify="space-between" align="flex-start" py={theme.other.spacing._12}>
                <Stack spacing={0}>
                  <Text variant="subhead-medium" c="gray2.6">
                    {t('deposits.completed.balanceBreakdown.credits')}
                  </Text>
                  <Text variant="body-large" c="gray2.6">
                    {t('deposits.completed.balanceBreakdown.creditsLabel')}
                  </Text>
                </Stack>
                {balance?.credits != null ? (
                  <Text variant="subhead-large" c="gray2.6">
                    {formatDollars(balance.credits)}
                  </Text>
                ) : (
                  <Text variant="body-large" c="gray2.6">
                    {t('deposits.completed.balanceUnavailable')}
                  </Text>
                )}
              </Flex>
            </Stack>
          </Paper>
        </Skeleton>
      </Box>

      {continueButton}
    </Stack>
  );
}

export default DepositCompleted;
